// Shared Dynamsoft Web TWAIN bootstrap.
//
// Both the photo-scanning scanner panel and the Devices discovery flow load the
// SDK. Previously each kept its own copy of this config, and each independently
// triggered Dynamsoft's blocking native dialogs on mount — the expired-license
// popup ("Sorry, your Dynamic Web TWAIN product key expired…") and the
// "service not found" prompt. Centralizing here lets us boot the SDK once AND
// suppress those modals in one place. The scanner is an *optional* input —
// operators can always fall back to uploading a PDF or images — so a missing or
// expired scanner must never interrupt the workflow with a modal. We surface
// our own calm, non-blocking status instead.

// ── Minimal Dynamsoft type surface (the shipped @types are loose `any`) ──────

export type DwtDevice = {
  name: string;
  displayName: string;
  deviceType: number;
};

export type DwtInstance = {
  GetDevicesAsync(deviceType?: number, refresh?: boolean): Promise<DwtDevice[]>;
  SelectDeviceAsync(device: DwtDevice): Promise<boolean>;
  AcquireImageAsync(config: Record<string, unknown>): Promise<boolean>;
  RegisterEvent(name: string, cb: (...args: unknown[]) => void): boolean | void;
  ConvertToBlob(
    indices: number[],
    type: number,
    success: (result: Blob, indices: number[], type: number) => void,
    failure: (errorCode: number, errorString: string) => void
  ): void;
  RemoveImage(index: number): boolean;
  CancelAllPendingTransfers(): boolean;
  HowManyImagesInBuffer: number;
};

export type DynamsoftStatic = {
  DWT: {
    ProductKey: string;
    ResourcesPath: string;
    ServiceInstallerLocation?: string;
    UseLocalService: boolean;
    AutoLoad: boolean;
    Containers: unknown[];
    CreateDWTObjectEx(
      cfg: { WebTwainId: string },
      success: (dwt: DwtInstance) => void,
      failure: (e: { code: number; message: string }) => void
    ): void;
    DeleteDWTObject(id: string): boolean;
    EnumDWT_ImageType: { IT_JPG: number };
    EnumDWT_CapSupportedSizes: { TWSS_NONE: number };
    // Internal surface we override to suppress native dialogs (loose `any` in
    // the shipped types). `ShowMessage` renders the license / trial / error
    // modal (it is what the SDK aliases `ShowLicenseDialog` to);
    // `OnWebTwainNotFound` renders the "download the service" prompt.
    ShowMessage?: (...args: unknown[]) => unknown;
    OnWebTwainNotFound?: unknown;
    RegisterEvent?: (name: string, cb: (...args: unknown[]) => void) => unknown;
  };
};

let sdkPromise: Promise<DynamsoftStatic> | null = null;

/**
 * Load + configure the Dynamsoft Web TWAIN SDK exactly once (memoized).
 *
 * On top of the standard headless config, this disables Dynamsoft's built-in
 * blocking dialogs (the expired-license popup and the "service not found"
 * prompt) so the SDK never throws a modal over the page. The app reflects
 * scanner availability through its own UI instead.
 */
export function loadDwtSdk(): Promise<DynamsoftStatic> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = (async () => {
    const mod = (await import('dwt')) as unknown as {
      default: DynamsoftStatic;
    };
    const Dynamsoft = mod.default;
    Dynamsoft.DWT.AutoLoad = false;
    Dynamsoft.DWT.Containers = [];
    Dynamsoft.DWT.UseLocalService = true;
    Dynamsoft.DWT.ResourcesPath = '/dwt-resources';
    Dynamsoft.DWT.ServiceInstallerLocation = '/dwt-resources/';
    if (process.env.NEXT_PUBLIC_DWT_LICENSE) {
      Dynamsoft.DWT.ProductKey = process.env.NEXT_PUBLIC_DWT_LICENSE;
    }
    // Suppress native blocking dialogs — we render our own calm status.
    const noop = () => {};
    Dynamsoft.DWT.ShowMessage = noop;
    Dynamsoft.DWT.OnWebTwainNotFound = noop;
    try {
      Dynamsoft.DWT.RegisterEvent?.('OnWebTwainError', noop);
    } catch {
      // Older builds may not expose RegisterEvent at the env level — the
      // ShowMessage override already covers the visible popup.
    }
    return Dynamsoft;
  })();
  return sdkPromise;
}

export type DwtErrorKind = 'license' | 'service' | 'unknown';

/**
 * Classify a Dynamsoft init failure so the UI can show an accurate reason.
 * - `license` — expired / invalid / unsupported product key (-2325..-2328, or
 *   a "product key" / "license" message such as the -2800 forbidden response)
 * - `service` — Dynamsoft Service not installed / not running (-28xx)
 * - `unknown` — anything else
 *
 * License is checked first so a product-key message in the -28xx range is not
 * mislabeled as a missing service.
 */
export function classifyDwtError(message: string): DwtErrorKind {
  if (/-?232[5-9]|product key|license/i.test(message)) return 'license';
  if (/-?28\d\d|service|installer/i.test(message)) return 'service';
  return 'unknown';
}
